import { api } from './client';
import type { ReferencesReport } from './persons';
import type { Account, AccountInput, LifecycleStatus, Page, UUID, WriteResult } from './types';

export interface AccountsListParams {
  agreement_id?: UUID;
  /** #277 (2026-06-08): multi-status filter via repeated query string. */
  status?: LifecycleStatus[];
  /** #274 (2026-06-08): orphan Accounts (BOTH agreement_id AND
   *  opened_by_policy_id NULL). By the accounts_ownership_chk
   *  constraint, orphans are always Inactive/Closed. Powers the
   *  Wave A.2 orphan picker on the new-Policy form. Mutually
   *  exclusive with agreement_id. */
  orphans_only?: boolean;
  q?: string;
  include_deleted?: boolean;
  // #46 Wave 1 (2026-06-12): per-column predicate filters (C-132).
  // agreement_q matches the EFFECTIVE agreement (direct or via Policy);
  // rto is multi-select (repeated keys).
  /** The bank's mnemonic (Banks list, 0112) — the list's first column. */
  mnemonic_q?: string;
  bank_q?: string;
  internal_q?: string;
  iban_q?: string;
  agreement_q?: string;
  policy_q?: string;
  rto?: boolean[];
  /** P41: a column key, '-' prefix to reverse (server-side). */
  sort?: string;
  limit?: number;
  offset?: number;
}

function qs(params: object): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    // #277 (2026-06-08): arrays expand to repeated query keys.
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item !== undefined && item !== null && item !== '') {
          usp.append(k, String(item));
        }
      }
    } else {
      usp.set(k, String(v));
    }
  }
  return usp.toString() ? `?${usp}` : '';
}

/** The Accounts list's "Export CSV" (Pedro 2026-10-10; Admin-only, C-335): the same
 * filters as the list, without paging — the export mirrors the screen. */
export function accountsExportHref(params: AccountsListParams): string {
  const { limit: _l, offset: _o, ...filters } = params;
  return `/accounts/export.xlsx${qs(filters)}`;
}

async function writeWithMaker<T>(
  call: () => Promise<{ data: T; status: number; headers: Headers }>,
): Promise<WriteResult<T>> {
  const r = await call();
  return {
    entity: r.data,
    pending: r.headers.get('X-Change-Request-Status') === 'pending',
    changeRequestId: r.headers.get('X-Change-Request-Id'),
  };
}

export const accountsApi = {
  list:    (p: AccountsListParams = {}) => api.get<Page<Account>>(`/accounts${qs(p)}`),
  get:     (id: UUID, includeDeleted = false) =>
    api.get<Account>(`/accounts/${id}${includeDeleted ? '?include_deleted=true' : ''}`),
  create:  (payload: AccountInput)      => writeWithMaker(() => api.postMeta<Account>('/accounts', payload)),
  update:  (id: UUID, p: AccountInput)  => writeWithMaker(() => api.patchMeta<Account>(`/accounts/${id}`, p)),
  delete:  (id: UUID)                   => writeWithMaker(() => api.deleteMeta<Account>(`/accounts/${id}`)),
  restore: (id: UUID)                   => writeWithMaker(() => api.postMeta<Account>(`/accounts/${id}/restore`)),
  references:    (id: UUID) => api.get<ReferencesReport>(`/accounts/${id}/references`),
  cascadeDelete: (id: UUID, refIds: Record<string, UUID[]>) =>
    writeWithMaker(() => api.postMeta<Account>(`/accounts/${id}/cascade-delete`, { ref_ids: refIds })),
  // A1 / #377 (2026-06-09): two detach endpoints - one per starting state.
  // Both transition the Account to orphan (Inactive, both FKs NULL).
  // Routed through maker-checker on the backend (standard UPDATE apply
  // path). RM filings queue a CR; Compliance/Admin auto-apply.
  detachFromAgreement: (id: UUID) =>
    writeWithMaker(() => api.postMeta<Account>(`/accounts/${id}/detach-from-agreement`)),
  detachFromPolicy:    (id: UUID) =>
    writeWithMaker(() => api.postMeta<Account>(`/accounts/${id}/detach-from-policy`)),

  /** #392 / #402 (2026-06-09): reattach an orphan Inactive Account to a
   *  target Agreement as Active Direct. Sibling of detachFromAgreement —
   *  closes the orphan-lifecycle symmetry on the Account side. Backend
   *  enforces: account is orphan + Inactive, target Agreement is alive +
   *  Active. Maker-checker routed: RM filings queue CR, Compliance/Admin
   *  auto-apply. Body: { agreement_id }. */
  attachToAgreement: (id: UUID, agreementId: UUID) =>
    writeWithMaker(() => api.postMeta<Account>(
      `/accounts/${id}/attach-to-agreement`,
      { agreement_id: agreementId },
    )),
};
